import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../utils/api';

// Day names in Turkish
const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

// Format date for display
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { 
        day: 'numeric', 
        month: 'short',
        year: 'numeric'
    });
};

// Format datetime for display
const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const SearchAnalyticsPage = () => {
    const { organizationId } = useParams();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [popularTerms, setPopularTerms] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [noResultTerms, setNoResultTerms] = useState([]);
    const [deviceStats, setDeviceStats] = useState([]);
    const [ctrStats, setCtrStats] = useState(null);
    const [dateFilter, setDateFilter] = useState('7'); // days or 'custom'
    const [customDateFrom, setCustomDateFrom] = useState('');
    const [customDateTo, setCustomDateTo] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    // Calculate date range based on filter
    const getDateRange = () => {
        if (dateFilter === 'custom' && customDateFrom && customDateTo) {
            return {
                date_from: customDateFrom,
                date_to: customDateTo
            };
        }
        
        const today = new Date();
        const from = new Date();
        from.setDate(today.getDate() - parseInt(dateFilter || '7'));
        
        return {
            date_from: from.toISOString().split('T')[0],
            date_to: today.toISOString().split('T')[0]
        };
    };

    // Fetch all analytics data
    const fetchAnalytics = async () => {
        if (!organizationId) return;
        
        setLoading(true);
        const { date_from, date_to } = getDateRange();
        
        try {
            const [statsRes, popularRes, recentRes, noResultRes, deviceRes, ctrRes] = await Promise.all([
                apiRequest(`/organizations/${organizationId}/search/stats?date_from=${date_from}&date_to=${date_to}`).catch(() => ({ success: false })),
                apiRequest(`/organizations/${organizationId}/search/popular?date_from=${date_from}&date_to=${date_to}&limit=20`).catch(() => ({ success: false })),
                apiRequest(`/organizations/${organizationId}/search/recent?limit=50`).catch(() => ({ success: false })),
                apiRequest(`/organizations/${organizationId}/search/no-results?limit=15`).catch(() => ({ success: false })),
                apiRequest(`/organizations/${organizationId}/search/devices?date_from=${date_from}&date_to=${date_to}`).catch(() => ({ success: false })),
                apiRequest(`/organizations/${organizationId}/search/ctr?date_from=${date_from}&date_to=${date_to}`).catch(() => ({ success: false }))
            ]);

            if (statsRes.success) setStats(statsRes.data);
            if (popularRes.success) setPopularTerms(popularRes.data || []);
            if (recentRes.success) setRecentSearches(recentRes.data || []);
            if (noResultRes.success) setNoResultTerms(noResultRes.data || []);
            if (deviceRes.success) setDeviceStats(deviceRes.data || []);
            if (ctrRes.success) setCtrStats(ctrRes.data);
        } catch (error) {
            console.error('Analytics fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // For custom dates, only fetch when both dates are set
        if (dateFilter === 'custom') {
            if (customDateFrom && customDateTo) {
                fetchAnalytics();
            }
        } else {
            fetchAnalytics();
        }
    }, [organizationId, dateFilter, customDateFrom, customDateTo]);

    // Get max value for bar chart scaling
    const getMaxHourlyValue = () => {
        if (!stats?.hourly) return 1;
        return Math.max(...stats.hourly.map(h => h.total_searches), 1);
    };

    // Get max value for day of week chart
    const getMaxDowValue = () => {
        if (!stats?.dayOfWeek) return 1;
        return Math.max(...stats.dayOfWeek.map(d => d.total_searches), 1);
    };

    // Device icon
    const getDeviceIcon = (type) => {
        switch (type) {
            case 'mobile':
                return (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                );
            case 'tablet':
                return (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                );
        }
    };

    if (loading && !stats) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center min-h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Arama Analitiği</h1>
                    <p className="text-gray-500 mt-1">Menüdeki arama trendlerini ve kullanıcı davranışlarını inceleyin</p>
                </div>
                
                {/* Date Filter */}
                <div className="flex flex-wrap items-center gap-2">
                    <label className="text-sm text-gray-600">Dönem:</label>
                    <select 
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                        <option value="7">Son 7 gün</option>
                        <option value="14">Son 14 gün</option>
                        <option value="30">Son 30 gün</option>
                        <option value="90">Son 90 gün</option>
                        <option value="custom">Özel Tarih</option>
                    </select>
                    
                    {dateFilter === 'custom' && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={customDateFrom}
                                onChange={(e) => setCustomDateFrom(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                max={customDateTo || undefined}
                            />
                            <span className="text-gray-400">-</span>
                            <input
                                type="date"
                                value={customDateTo}
                                onChange={(e) => setCustomDateTo(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                min={customDateFrom || undefined}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Searches */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Toplam Arama</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats?.summary?.total_searches?.toLocaleString('tr-TR') || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Unique Terms */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Farklı Terim</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats?.summary?.unique_terms?.toLocaleString('tr-TR') || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Click Through Rate */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Tıklama Oranı</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                %{ctrStats?.ctr_percentage || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Discount Searches */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">İndirim Aramaları</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                                {stats?.summary?.discount_searches?.toLocaleString('tr-TR') || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                    {[
                        { id: 'overview', name: 'Genel Bakış' },
                        { id: 'popular', name: 'Popüler Aramalar' },
                        { id: 'recent', name: 'Son Aramalar' },
                        { id: 'noresult', name: 'Sonuçsuz Aramalar' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-orange-500 text-orange-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Hourly Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Saatlik Dağılım</h3>
                        <div className="space-y-2">
                            {Array.from({ length: 24 }, (_, i) => {
                                const hourData = stats?.hourly?.find(h => h.hour_of_day === i);
                                const value = hourData?.total_searches || 0;
                                const maxValue = getMaxHourlyValue();
                                const percentage = (value / maxValue) * 100;
                                
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="w-12 text-xs text-gray-500 text-right">
                                            {String(i).padStart(2, '0')}:00
                                        </span>
                                        <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                                            <div 
                                                className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded transition-all"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                        <span className="w-10 text-xs text-gray-600 text-right">
                                            {value}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Day of Week Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Günlük Dağılım</h3>
                        <div className="space-y-3">
                            {dayNames.map((day, index) => {
                                const dayData = stats?.dayOfWeek?.find(d => d.search_day_of_week === index);
                                const value = dayData?.total_searches || 0;
                                const maxValue = getMaxDowValue();
                                const percentage = (value / maxValue) * 100;
                                
                                return (
                                    <div key={index} className="flex items-center gap-3">
                                        <span className="w-20 text-sm text-gray-600">{day}</span>
                                        <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                                            <div 
                                                className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg transition-all flex items-center justify-end pr-2"
                                                style={{ width: `${Math.max(percentage, 5)}%` }}
                                            >
                                                {percentage > 20 && (
                                                    <span className="text-xs text-white font-medium">{value}</span>
                                                )}
                                            </div>
                                        </div>
                                        {percentage <= 20 && (
                                            <span className="w-10 text-sm text-gray-600 text-right">{value}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Device Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cihaz Dağılımı</h3>
                        <div className="space-y-4">
                            {deviceStats.length > 0 ? (
                                deviceStats.map((device) => {
                                    const total = deviceStats.reduce((sum, d) => sum + d.search_count, 0);
                                    const percentage = total > 0 ? ((device.search_count / total) * 100).toFixed(1) : 0;
                                    const colors = {
                                        mobile: 'from-green-400 to-green-600',
                                        tablet: 'from-blue-400 to-blue-600',
                                        desktop: 'from-gray-400 to-gray-600'
                                    };
                                    
                                    return (
                                        <div key={device.device_type} className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                                                {getDeviceIcon(device.device_type)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium text-gray-700 capitalize">
                                                        {device.device_type === 'mobile' ? 'Mobil' : 
                                                         device.device_type === 'tablet' ? 'Tablet' : 'Masaüstü'}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        {device.search_count} ({percentage}%)
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full bg-gradient-to-r ${colors[device.device_type]} rounded-full`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
                            )}
                        </div>
                    </div>

                    {/* Daily Trend */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Günlük Trend</h3>
                        {stats?.daily?.length > 0 ? (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {stats.daily.slice().reverse().map((day, index) => (
                                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                        <span className="text-sm text-gray-600">{formatDate(day.search_date)}</span>
                                        <div className="flex items-center gap-4">
                                            <span className="text-sm text-gray-500">
                                                {day.unique_terms} terim
                                            </span>
                                            <span className="text-sm font-medium text-gray-900">
                                                {day.total_searches} arama
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'popular' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900">En Çok Aranan Terimler</h3>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcıların en sık aradığı kelimeler</p>
                    </div>
                    {popularTerms.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {popularTerms.map((term, index) => (
                                <div key={index} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                            index < 3 ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {index + 1}
                                        </span>
                                        <div>
                                            <span className="font-medium text-gray-900">{term.search_term}</span>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Son arama: {formatDateTime(term.last_searched_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6 text-sm">
                                        <div className="text-right">
                                            <span className="font-semibold text-gray-900">{term.search_count}</span>
                                            <span className="text-gray-500 ml-1">arama</span>
                                        </div>
                                        <div className="text-right text-gray-500">
                                            <span>~{Math.round(term.avg_result_count || 0)} sonuç</span>
                                        </div>
                                        {term.click_through_count > 0 && (
                                            <div className="text-right text-green-600">
                                                <span>{term.click_through_count} tıklama</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <p>Henüz arama verisi yok</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'recent' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900">Son Aramalar</h3>
                        <p className="text-sm text-gray-500 mt-1">Gerçek zamanlı arama aktivitesi</p>
                    </div>
                    {recentSearches.length > 0 ? (
                        <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                            {recentSearches.map((search, index) => (
                                <div key={index} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                            search.search_type === 'discount' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                                        }`}>
                                            {search.search_type === 'discount' ? (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-900">{search.search_term}</span>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-gray-400">
                                                    {formatDateTime(search.created_at)}
                                                </span>
                                                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded capitalize">
                                                    {search.device_type === 'mobile' ? 'Mobil' : 
                                                     search.device_type === 'tablet' ? 'Tablet' : 'Masaüstü'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                        <div className="text-right text-gray-500">
                                            <span>{search.result_count} sonuç</span>
                                        </div>
                                        {search.clicked_result_id && (
                                            <div className="flex items-center gap-1 text-green-600">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                                </svg>
                                                <span className="text-xs capitalize">
                                                    {search.clicked_result_type === 'product' ? 'Ürün' : 'Kategori'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p>Henüz arama yapılmamış</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'noresult' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900">Sonuçsuz Aramalar</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Bu aramalar hiç sonuç döndürmedi - menünüze ekleyebileceğiniz yeni ürün fikirleri!
                        </p>
                    </div>
                    {noResultTerms.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {noResultTerms.map((term, index) => (
                                <div key={index} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-600">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-900">{term.search_term}</span>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Son arama: {formatDateTime(term.last_searched_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-red-600">{term.search_count}</span>
                                        <span className="text-sm text-gray-500">kez arandı</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p>Harika! Tüm aramalar sonuç döndürüyor</p>
                        </div>
                    )}
                </div>
            )}
        </div>
        </AdminLayout>
    );
};

export default SearchAnalyticsPage;

